import React from 'react'
import { createRoot, Root } from 'react-dom/client'
import { SlashProvider, slashFactory } from '@milkdown/kit/plugin/slash'
import { TextSelection, type PluginView, type Selection } from '@milkdown/kit/prose/state'
import { $ctx } from '@milkdown/kit/utils'

import { RichTextEditorCopy } from '@/components/common/RichTextEditor/copy'
import {
    executeRichTextEditorAction,
    getRichTextEditorActionDefinitions,
    RichTextEditorActionDefinition,
} from '@/components/common/RichTextEditor/editor/actions'
import { cn } from '@/components/new/lib/utils'
import type { RichTextEditorMode } from '@/components/common/RichTextEditor/types'

import type { Ctx } from '@milkdown/kit/ctx'
import type { EditorView } from '@milkdown/kit/prose/view'

type SlashMenuItem = RichTextEditorActionDefinition & {
    onRun: (ctx: Ctx) => void
}

type SlashMenuGroup = {
    key: string
    label: string
    range: [start: number, end: number]
    items: Array<SlashMenuItem & { index: number }>
}

type RichTextEditorMenuConfig = {
    codeBlock: boolean
    portalTarget: string
    headings: boolean
    copy: RichTextEditorCopy
    imageUploadEnabled: boolean
    mode: RichTextEditorMode
    quote: boolean
    taskList: boolean
}

type RichTextEditorActions = {
    openImagePicker: () => void
}

export const richTextEditorActionsCtx = $ctx<RichTextEditorActions, 'richTextEditorActionsCtx'>(
    {
        openImagePicker: () => undefined,
    },
    'richTextEditorActionsCtx'
)

export type RichTextEditorMenuApi = {
    show: (pos: number) => void
    hide: () => void
}

export const richTextEditorMenuApiCtx = $ctx<RichTextEditorMenuApi, 'richTextEditorMenuApiCtx'>(
    {
        show: () => undefined,
        hide: () => undefined,
    },
    'richTextEditorMenuApiCtx'
)

const getGroups = (filter: string, config: RichTextEditorMenuConfig, ctx: Ctx) => {
    const copy = config.copy

    const groups = [
        {
            key: 'text',
            label: copy.groups.text,
            items: getRichTextEditorActionDefinitions({
                codeBlock: config.codeBlock,
                copy,
                headings: config.headings,
                imageUploadEnabled: config.imageUploadEnabled,
                mode: config.mode,
                quote: config.quote,
                taskList: config.taskList,
            })
                .filter((item) => item.group === 'text')
                .map((item) => ({
                    ...item,
                    onRun: () =>
                        executeRichTextEditorAction(item.id, ctx, {
                            openImagePicker: () => ctx.get(richTextEditorActionsCtx.key).openImagePicker(),
                        }),
                })),
        },
        {
            key: 'lists',
            label: copy.groups.lists,
            items: getRichTextEditorActionDefinitions({
                codeBlock: config.codeBlock,
                copy,
                headings: config.headings,
                imageUploadEnabled: config.imageUploadEnabled,
                mode: config.mode,
                quote: config.quote,
                taskList: config.taskList,
            })
                .filter((item) => item.group === 'lists')
                .map((item) => ({
                    ...item,
                    onRun: () =>
                        executeRichTextEditorAction(item.id, ctx, {
                            openImagePicker: () => ctx.get(richTextEditorActionsCtx.key).openImagePicker(),
                        }),
                })),
        },
        {
            key: 'other',
            label: copy.groups.other,
            items: getRichTextEditorActionDefinitions({
                codeBlock: config.codeBlock,
                copy,
                headings: config.headings,
                imageUploadEnabled: config.imageUploadEnabled,
                mode: config.mode,
                quote: config.quote,
                taskList: config.taskList,
            })
                .filter((item) => item.group === 'other')
                .map((item) => ({
                    ...item,
                    onRun: () =>
                        executeRichTextEditorAction(item.id, ctx, {
                            openImagePicker: () => ctx.get(richTextEditorActionsCtx.key).openImagePicker(),
                        }),
                })),
        },
    ]
        .map((group) => ({
            ...group,
            items: group.items.filter((item) => item.label.toLowerCase().includes(filter.toLowerCase())),
        }))
        .filter((group) => group.items.length > 0)

    let index = 0

    const indexedGroups: SlashMenuGroup[] = groups.map((group) => {
        const items = group.items.map((item) => {
            const nextItem = {
                ...item,
                index,
            }

            index += 1
            return nextItem
        })

        return {
            ...group,
            range: [items[0]?.index ?? 0, (items.at(-1)?.index ?? -1) + 1],
            items,
        }
    })

    return {
        groups: indexedGroups,
        size: index,
    }
}

const SlashMenu = ({
    ctx,
    config,
    filter,
    hide,
    show,
}: {
    ctx: Ctx
    config: RichTextEditorMenuConfig
    filter: string
    hide: () => void
    show: boolean
}) => {
    const { groups, size } = getGroups(filter, config, ctx)
    const [hoverIndex, setHoverIndex] = React.useState(0)
    const hostRef = React.useRef<HTMLDivElement>(null)

    React.useEffect(() => {
        if (!show) {
            return
        }

        if (size === 0) {
            hide()
            return
        }

        if (hoverIndex >= size) {
            setHoverIndex(0)
        }
    }, [hide, hoverIndex, show, size])

    const scrollToIndex = React.useCallback((index: number) => {
        const target = hostRef.current?.querySelector<HTMLElement>(`[data-index="${index}"]`)
        const scrollRoot = hostRef.current?.querySelector<HTMLElement>('.rich-text-editor-slash-groups')

        if (!target || !scrollRoot) {
            return
        }

        scrollRoot.scrollTop = target.offsetTop - scrollRoot.offsetTop
    }, [])

    const runByIndex = React.useCallback(
        (index: number) => {
            const item = groups.flatMap((group) => group.items).find((candidate) => candidate.index === index)
            if (!item) {
                return
            }

            item.onRun(ctx)
            hide()
        },
        [ctx, groups, hide]
    )

    React.useEffect(() => {
        if (!show) {
            return
        }

        const onKeyDown = (event: KeyboardEvent) => {
            switch (event.key) {
                case 'Escape':
                    event.preventDefault()
                    hide()
                    return
                case 'ArrowDown':
                    event.preventDefault()
                    setHoverIndex((currentIndex) => {
                        const nextIndex = Math.min(size - 1, currentIndex + 1)
                        scrollToIndex(nextIndex)
                        return nextIndex
                    })
                    return
                case 'ArrowUp':
                    event.preventDefault()
                    setHoverIndex((currentIndex) => {
                        const nextIndex = Math.max(0, currentIndex - 1)
                        scrollToIndex(nextIndex)
                        return nextIndex
                    })
                    return
                case 'Enter':
                    event.preventDefault()
                    runByIndex(hoverIndex)
                    return
            }
        }

        window.addEventListener('keydown', onKeyDown, { capture: true })
        return () => window.removeEventListener('keydown', onKeyDown, { capture: true })
    }, [hide, hoverIndex, runByIndex, scrollToIndex, show, size])

    return (
        <div
            ref={hostRef}
            className="bg-popover text-popover-foreground ring-foreground/10 flex flex-col rounded-lg text-sm shadow-md ring-1"
            onPointerDown={(event) => event.preventDefault()}
        >
            <div className="rich-text-editor-slash-groups">
                {groups.map((group) => (
                    <div key={group.key} className="rich-text-editor-slash-group">
                        <h6 className="text-muted-foreground px-3 pb-1 text-left text-xs font-medium">{group.label}</h6>
                        <ul>
                            {group.items.map((item) => {
                                const Icon = item.icon

                                return (
                                    <li
                                        key={item.id}
                                        data-index={item.index}
                                        className={cn(
                                            'flex cursor-pointer items-center gap-3 rounded-md px-3 py-3.5 leading-none transition-colors',
                                            hoverIndex === item.index && 'bg-accent text-accent-foreground'
                                        )}
                                        onPointerEnter={() => setHoverIndex(item.index)}
                                        onPointerUp={() => runByIndex(item.index)}
                                    >
                                        <span className="flex size-5 shrink-0 items-center justify-center">
                                            <Icon className="size-4 shrink-0" />
                                        </span>
                                        <span className="min-w-0 flex-1 leading-6">{item.label}</span>
                                    </li>
                                )
                            })}
                        </ul>
                    </div>
                ))}
            </div>
        </div>
    )
}

export const richTextEditorMenu = slashFactory('RICH_TEXT_EDITOR_SLASH_MENU')

export const configureRichTextEditorMenu = (ctx: Ctx, config: RichTextEditorMenuConfig) => {
    ctx.set(richTextEditorMenu.key, {
        view: (view) => new RichTextEditorMenuView(ctx, view, config),
    })
}

class RichTextEditorMenuView implements PluginView {
    readonly #content: HTMLElement
    readonly #root: Root
    readonly #provider: SlashProvider
    #filter = ''
    #show = false
    #view: EditorView
    #programmaticPos: number | null = null

    constructor(ctx: Ctx, view: EditorView, config: RichTextEditorMenuConfig) {
        const target = document.getElementById(config.portalTarget)

        if (!target) {
            throw new Error('RichTextEditor portal target not found')
        }

        this.#content = document.createElement('div')
        this.#content.classList.add('rich-text-editor-slash-menu')
        target.appendChild(this.#content)

        this.#root = createRoot(this.#content)
        this.#view = view

        const renderMenu = () => {
            this.#root.render(
                <SlashMenu ctx={ctx} config={config} filter={this.#filter} hide={this.hide} show={this.#show} />
            )
        }

        this.#provider = new SlashProvider({
            content: document.createElement('div'),
            debounce: 20,
            shouldShow: (editorView) => {
                const currentText = this.#provider.getContent(editorView, (node) =>
                    ['paragraph', 'heading'].includes(node.type.name)
                )

                if (currentText == null || !isSelectionAtEndOfNode(editorView.state.selection)) {
                    return false
                }

                this.#filter = currentText.startsWith('/') ? currentText.slice(1) : currentText
                renderMenu()

                if (typeof this.#programmaticPos === 'number') {
                    const maxSize = editorView.state.doc.nodeSize - 2
                    const validPos = Math.min(this.#programmaticPos, maxSize)

                    if (
                        editorView.state.doc.resolve(validPos).node() !==
                        editorView.state.doc.resolve(editorView.state.selection.from).node()
                    ) {
                        this.#programmaticPos = null
                        return false
                    }

                    return true
                }

                return currentText.startsWith('/')
            },
            offset: 10,
        })

        this.#provider.onShow = () => {
            const coords = this.#view.coordsAtPos(this.#view.state.selection.from)

            Object.assign(this.#content.style, {
                visibility: 'hidden',
                display: 'block',
                position: 'fixed',
                left: `${coords.left}px`,
                top: '0px',
                zIndex: '9999',
            })

            this.#show = true
            renderMenu()

            requestAnimationFrame(() => {
                const menuHeight = this.#content.offsetHeight
                const viewportHeight = window.innerHeight
                const spaceBelow = viewportHeight - coords.bottom
                const spaceAbove = coords.top
                const showAbove = spaceAbove > spaceBelow && spaceAbove > menuHeight

                Object.assign(this.#content.style, {
                    top: showAbove ? `${coords.top - menuHeight - 8}px` : `${coords.bottom + 8}px`,
                    visibility: 'visible',
                })
            })
        }

        this.#provider.onHide = () => {
            this.#show = false
            this.#content.style.display = 'none'
            renderMenu()
        }

        this.update(view)

        ctx.set(richTextEditorMenuApiCtx.key, {
            show: (pos) => this.show(pos),
            hide: () => this.hide(),
        })
    }

    update = (view: EditorView) => {
        this.#view = view
        this.#provider.update(view)
    }

    show = (pos: number) => {
        this.#programmaticPos = pos
        this.#filter = ''
        this.#provider.show()
    }

    hide = () => {
        this.#programmaticPos = null
        this.#provider.hide()
    }

    destroy = () => {
        this.#provider.destroy()
        this.#root.unmount()
        this.#content.remove()
    }
}

const isSelectionAtEndOfNode = (selection: Selection) => {
    if (!(selection instanceof TextSelection)) {
        return false
    }

    return selection.$head.parentOffset === selection.$head.parent.content.size
}
